import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Coins,
  ArrowDownToLine,
  ArrowUpFromLine,
  Repeat,
  ShieldAlert,
  Loader2,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  X,
} from "lucide-react";

function fmtUsd(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (abs >= 1) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 6 })}`;
}

function fmtPct(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const s = v >= 0 ? "+" : "";
  return `${s}${v.toFixed(2)}%`;
}

export default function Crypto() {
  const { user } = useAuth();
  const [tab, setTab] = useState("buy");
  const [coins, setCoins] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(true);

  // Form state
  const [coin, setCoin] = useState("btc");
  const [toCoin, setToCoin] = useState("eth");
  const [amount, setAmount] = useState("100");
  const [wallet, setWallet] = useState("");

  const [generating, setGenerating] = useState(false);
  const [widgetUrl, setWidgetUrl] = useState(null);
  const [widgetFlow, setWidgetFlow] = useState(null);

  const kycApproved = user?.kyc_status === "approved";

  useEffect(() => {
    let mounted = true;
    api.get("/crypto/coins").then(({ data }) => {
      if (!mounted) return;
      setCoins(data.coins || []);
    });
    const loadPrices = async () => {
      try {
        const { data } = await api.get("/crypto/prices");
        if (!mounted) return;
        setPrices(data.coins || []);
      } catch (_) {
        // silent — KYC not required for live coin list but prices needs auth
      } finally {
        if (mounted) setLoadingPrices(false);
      }
    };
    loadPrices();
    const interval = setInterval(loadPrices, 60_000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  const selectedCoinMeta = useMemo(
    () => coins.find((c) => c.code === coin) || null,
    [coins, coin]
  );
  const toCoinMeta = useMemo(
    () => coins.find((c) => c.code === toCoin) || null,
    [coins, toCoin]
  );

  const amountLabel =
    tab === "buy"
      ? "Spend (USD)"
      : tab === "sell"
      ? `Sell amount (${selectedCoinMeta?.symbol || "—"})`
      : `From amount (${selectedCoinMeta?.symbol || "—"})`;

  const walletLabel =
    tab === "buy"
      ? `Your ${selectedCoinMeta?.symbol || "wallet"} receive address`
      : `Your ${selectedCoinMeta?.symbol || "wallet"} source address`;

  const needsWallet = tab === "buy" || tab === "swap";

  const handleOpenWidget = async () => {
    if (!kycApproved) {
      toast.error("Identity verification is required before transacting in crypto.");
      return;
    }
    const value = Number(amount);
    if (!value || value <= 0) {
      toast.error("Enter an amount greater than zero.");
      return;
    }
    if (needsWallet && !wallet.trim()) {
      toast.error("Enter a valid wallet address.");
      return;
    }
    if (tab === "swap" && (!toCoin || toCoin === coin)) {
      toast.error("Pick a different destination coin to swap into.");
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        flow: tab,
        coin,
        amount: value,
        wallet_address: needsWallet ? wallet.trim() : undefined,
        to_coin: tab === "swap" ? toCoin : undefined,
      };
      const { data } = await api.post("/crypto/sign-url", payload);
      setWidgetUrl(data.url);
      setWidgetFlow(tab);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const closeWidget = () => {
    setWidgetUrl(null);
    setWidgetFlow(null);
  };

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="text-eyebrow text-white/55">Crypto exchange</div>
        <div className="flex items-center gap-3 mt-1">
          <Coins className="w-7 h-7 text-[#B58957]" />
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white">
            Buy, sell, and swap crypto
          </h1>
        </div>
        <p className="text-white/65 mt-3 max-w-2xl">
          Powered by MoonPay. FinVault is fully non-custodial — funds settle
          directly to your own external wallet. We never hold your coins or
          private keys.
        </p>

        {!kycApproved && (
          <div
            className="mt-6 fv-card p-5 flex items-start gap-3 border border-[#E27A6A]/40"
            data-testid="crypto-kyc-warning"
          >
            <ShieldAlert className="w-5 h-5 text-[#E27A6A] mt-0.5" />
            <div>
              <div className="font-semibold text-white">
                Identity verification required
              </div>
              <div className="text-sm text-white/70 mt-1">
                Crypto trading is unlocked only after KYC approval. You can
                still browse live market prices below.
              </div>
            </div>
          </div>
        )}

        {/* Live market */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl text-white">Live market</h2>
            <span className="text-xs text-white/45">
              {loadingPrices ? "Loading…" : "Refreshes every 60s · CoinGecko"}
            </span>
          </div>
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
            data-testid="crypto-prices-grid"
          >
            {(prices.length ? prices : coins).map((c) => {
              const up = (c.change_24h ?? 0) >= 0;
              return (
                <button
                  key={c.code}
                  onClick={() => setCoin(c.code)}
                  data-testid={`crypto-price-${c.code}`}
                  className={`text-left fv-card p-4 transition-colors ${
                    coin === c.code
                      ? "ring-2 ring-[#B58957]"
                      : "hover:border-white/20"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-eyebrow text-white/50">
                      {c.symbol}
                    </span>
                    {c.change_24h != null && (
                      <span
                        className={`text-xs flex items-center gap-1 ${
                          up ? "text-[#7BBF8E]" : "text-[#E27A6A]"
                        }`}
                      >
                        {up ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        {fmtPct(c.change_24h)}
                      </span>
                    )}
                  </div>
                  <div className="font-display text-base font-semibold text-white mt-1">
                    {c.name}
                  </div>
                  <div className="text-sm text-white/75 mt-1">
                    {fmtUsd(c.price_usd)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Trade panel */}
        <div className="mt-10 fv-card p-6 md:p-8">
          <Tabs value={tab} onValueChange={setTab} data-testid="crypto-flow-tabs">
            <TabsList className="bg-white/[0.06]">
              <TabsTrigger value="buy" data-testid="crypto-tab-buy">
                <ArrowDownToLine className="w-4 h-4 mr-2" />
                Buy
              </TabsTrigger>
              <TabsTrigger value="sell" data-testid="crypto-tab-sell">
                <ArrowUpFromLine className="w-4 h-4 mr-2" />
                Sell
              </TabsTrigger>
              <TabsTrigger value="swap" data-testid="crypto-tab-swap">
                <Repeat className="w-4 h-4 mr-2" />
                Swap
              </TabsTrigger>
            </TabsList>

            <TabsContent value={tab} className="mt-6">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-white/70">
                    {tab === "swap" ? "From coin" : "Coin"}
                  </Label>
                  <Select value={coin} onValueChange={setCoin}>
                    <SelectTrigger
                      className="bg-[#0A130E] border-white/10 text-white mt-2"
                      data-testid="crypto-coin-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A130E] border-white/10 text-white">
                      {coins.map((c) => (
                        <SelectItem
                          key={c.code}
                          value={c.code}
                          className="focus:bg-white/[0.06]"
                          data-testid={`crypto-coin-option-${c.code}`}
                        >
                          {c.symbol} · {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {tab === "swap" && (
                  <div>
                    <Label className="text-white/70">To coin</Label>
                    <Select value={toCoin} onValueChange={setToCoin}>
                      <SelectTrigger
                        className="bg-[#0A130E] border-white/10 text-white mt-2"
                        data-testid="crypto-tocoin-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A130E] border-white/10 text-white">
                        {coins
                          .filter((c) => c.code !== coin)
                          .map((c) => (
                            <SelectItem
                              key={c.code}
                              value={c.code}
                              className="focus:bg-white/[0.06]"
                              data-testid={`crypto-tocoin-option-${c.code}`}
                            >
                              {c.symbol} · {c.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-white/70">{amountLabel}</Label>
                  <Input
                    type="number"
                    min="0"
                    step={tab === "buy" ? "10" : "0.0001"}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="bg-[#0A130E] border-white/10 text-white mt-2"
                    data-testid="crypto-amount-input"
                  />
                </div>

                {needsWallet && (
                  <div className={tab === "swap" ? "" : "md:col-span-2"}>
                    <Label className="text-white/70">{walletLabel}</Label>
                    <Input
                      placeholder={`Paste your ${
                        selectedCoinMeta?.symbol || ""
                      } address`}
                      value={wallet}
                      onChange={(e) => setWallet(e.target.value)}
                      className="bg-[#0A130E] border-white/10 text-white mt-2 font-mono text-xs"
                      data-testid="crypto-wallet-input"
                    />
                    <p className="text-xs text-white/45 mt-2">
                      You must control this wallet. MoonPay sends/receives
                      funds directly to/from this address. FinVault never sees
                      your private keys.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button
                  onClick={handleOpenWidget}
                  disabled={generating || !kycApproved}
                  className="bg-[#B58957] hover:bg-[#9C7548] text-[#0A130E] font-medium rounded-lg"
                  data-testid="crypto-open-widget"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Preparing secure session…
                    </>
                  ) : (
                    <>
                      Open {tab === "buy" ? "Buy" : tab === "sell" ? "Sell" : "Swap"} widget
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
                <span className="text-xs text-white/45">
                  Sandbox · No real funds will move until you switch to
                  production keys.
                </span>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 text-xs text-white/45 max-w-3xl">
          FinVault provides a non-custodial gateway to MoonPay's regulated
          crypto ramps. Crypto assets are volatile and may lose value.
          Transactions are settled directly between you and MoonPay; FinVault
          does not custody crypto, hold private keys, or guarantee execution.
          Network and MoonPay fees apply.
        </div>
      </div>

      {/* Widget modal */}
      {widgetUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="crypto-widget-modal"
        >
          <div className="relative bg-[#0A130E] border border-white/10 rounded-xl w-full max-w-2xl h-[80vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="font-display text-white text-sm">
                MoonPay ·{" "}
                {widgetFlow === "buy"
                  ? "Buy crypto"
                  : widgetFlow === "sell"
                  ? "Sell crypto"
                  : "Swap crypto"}
              </div>
              <button
                onClick={closeWidget}
                className="text-white/60 hover:text-white p-2 rounded-md hover:bg-white/[0.06]"
                data-testid="crypto-widget-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <iframe
              title="MoonPay widget"
              src={widgetUrl}
              className="flex-1 w-full bg-white"
              allow="accelerometer; autoplay; camera; gyroscope; payment"
              data-testid="crypto-widget-iframe"
            />
          </div>
        </div>
      )}
    </AppLayout>
  );
}
